import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const EventsSection = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('upcoming');

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    // Auto-select the first tab that has at least 3 events
    const activeTabs = getActiveTabs();
    if (activeTabs.length > 0 && !activeTabs.find(tab => tab.key === activeTab)) {
      setActiveTab(activeTabs[0].key);
    }
  }, [events]);

  const fetchEvents = async () => {
    try {
      // Try multiple possible API paths
      const apiPaths = [
        './admin/api/events.php',
        '/admin/api/events.php',
        'admin/api/events.php'
      ];
      
      let response = null;
      let data = null;
      
      for (const path of apiPaths) {
        try {
          response = await fetch(path);
          if (response.ok) {
            data = await response.json();
            break;
          }
        } catch (err) {
          console.warn(`Failed to fetch from ${path}:`, err);
          continue;
        }
      }
      
      if (response && response.ok && data && data.success) {
        setEvents(data.data);
      } else {
        console.error('Failed to fetch events from any path');
        setEvents([]);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredEvents = () => {
    const now = new Date();
    return events.filter(event => {
      const eventDate = new Date(event.event_date);
      
      switch (activeTab) {
        case 'upcoming':
          return eventDate >= now && event.status === 'upcoming';
        case 'ongoing':
          return event.status === 'ongoing';
        case 'completed':
          return eventDate < now || event.status === 'completed';
        default:
          return true;
      }
    });
  };

  const getEventCount = (tabType) => {
    const now = new Date();
    return events.filter(event => {
      const eventDate = new Date(event.event_date);
      
      switch (tabType) {
        case 'upcoming':
          return eventDate >= now && event.status === 'upcoming';
        case 'ongoing':
          return event.status === 'ongoing';
        case 'completed':
          return eventDate < now || event.status === 'completed';
        default:
          return true;
      }
    }).length;
  };

  const getActiveTabs = () => {
    const tabs = [
      { key: 'upcoming', label: 'Upcoming Events', icon: 'fas fa-clock' },
      { key: 'ongoing', label: 'Ongoing Events', icon: 'fas fa-play' },
      { key: 'completed', label: 'Past Events', icon: 'fas fa-check' }
    ];
    
    return tabs.filter(tab => getEventCount(tab.key) >= 3);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const time = new Date(`2000-01-01T${timeString}`);
    return time.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  if (loading) {
    return (
      <section className="py-8 sm:py-10 lg:py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12 sm:py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600 text-lg">Loading events...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-8 sm:py-10 lg:py-12 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-800 mb-3 sm:mb-4">School Events</h2>
          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">Stay updated with our latest events and activities</p>
        </div>

        {/* Event Tabs */}
        {getActiveTabs().length > 0 && (
          <div className="flex justify-center mb-6 sm:mb-8">
            <div className="bg-gray-100 p-1 rounded-xl sm:rounded-2xl inline-flex flex-wrap justify-center gap-1 sm:gap-0">
              {getActiveTabs().map((tab) => (
                <button 
                  key={tab.key}
                  className={`px-3 sm:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl font-semibold transition-all duration-300 text-xs sm:text-sm lg:text-base ${
                    activeTab === tab.key 
                      ? 'bg-white text-blue-600 shadow-lg' 
                      : 'text-gray-600 hover:text-blue-600'
                  }`}
                  onClick={() => setActiveTab(tab.key)}
                >
                  <i className={`${tab.icon} mr-1 sm:mr-2`}></i>
                  <span className="hidden sm:inline">{tab.label}</span>
                  <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Events Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
          {getFilteredEvents().length === 0 ? (
            <div className="col-span-full">
              <div className="text-center py-12 sm:py-16">
                <div className="bg-white rounded-2xl sm:rounded-3xl shadow-lg p-6 sm:p-8 lg:p-12 max-w-sm sm:max-w-md mx-auto">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                    <i className="fas fa-calendar-times text-2xl sm:text-3xl text-blue-500"></i>
                  </div>
                  <h4 className="text-lg sm:text-xl font-bold text-gray-800 mb-2 sm:mb-3">
                    No Events Available
                  </h4>
                  <p className="text-sm sm:text-base text-gray-600">
                    We're working on bringing you amazing events soon!
                  </p>
                </div>
              </div>
            </div>
          ) : (
            getFilteredEvents().map((event) => (
              <div 
                key={event.id} 
                className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 overflow-hidden border border-gray-100 group relative cursor-pointer"
                onClick={() => navigate(`/event-details/${event.id}`)}
              >
                  {/* Featured Badge */}
                  {event.featured && (
                    <div className="absolute top-2 right-2 z-10">
                      <span className="bg-gradient-to-r from-yellow-400 to-orange-500 text-black px-2 py-1 rounded-full text-xs font-bold shadow-md">
                        <i className="fas fa-star"></i>
                      </span>
                    </div>
                  )}
                  
                  {/* Event Image */}
                  {event.image_path ? (
                    <div className="relative h-40 sm:h-48 md:h-52 overflow-hidden">
                      <img 
                        src={`https://pakschoolandcollege.com/admin/${event.image_path}`} 
                        alt={event.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent"></div>
                    </div>
                  ) : (
                    <div className="h-40 sm:h-48 md:h-52 bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                      <i className="fas fa-calendar-alt text-white text-xl sm:text-2xl"></i>
                    </div>
                  )}
                  
                  {/* Event Date Badge */}
                  <div className="absolute top-2 left-2 z-10">
                    <div className="bg-white/95 backdrop-blur-sm rounded-lg px-1.5 sm:px-2 py-1 text-center shadow-md">
                      <div className="text-xs sm:text-sm font-bold text-gray-800">
                        {new Date(event.event_date).getDate()}
                      </div>
                      <div className="text-xs text-gray-600">
                        {new Date(event.event_date).toLocaleDateString('en-US', { month: 'short' })}
                      </div>
                    </div>
                  </div>
                  
                  {/* Event Content */}
                  <div className="p-2.5 sm:p-3">
                    <h3 className="text-xs sm:text-sm font-bold text-gray-800 mb-2 line-clamp-2 group-hover:text-blue-600 transition-colors">
                      {event.name}
                    </h3>
                    
                    {/* Event Details */}
                    <div className="space-y-1 mb-2 sm:mb-3">
                      {event.event_time && (
                        <div className="flex items-center text-xs text-gray-500">
                          <i className="fas fa-clock w-3 mr-1 text-blue-500"></i>
                          <span>{formatTime(event.event_time)}</span>
                        </div>
                      )}
                      
                      {event.location && (
                        <div className="flex items-center text-xs text-gray-500">
                          <i className="fas fa-map-marker-alt w-3 mr-1 text-red-500"></i>
                          <span className="truncate">{event.location}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Status Badge and Action */}
                    <div className="flex items-center justify-between">
                      <span className={`px-1.5 sm:px-2 py-1 rounded-full text-xs font-semibold ${
                        event.status === 'upcoming' ? 'bg-blue-100 text-blue-800' :
                        event.status === 'ongoing' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {event.status.charAt(0).toUpperCase() + event.status.slice(1)}
                      </span>
                      
                      <button 
                        className="bg-blue-600 text-white px-2 sm:px-3 py-1 rounded-lg text-xs font-semibold hover:bg-blue-700 transition-colors duration-200"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/event-details/${event.id}`);
                        }}
                      >
                        View
                      </button>
                    </div>
                  </div>
                </div>
            ))
          )}
        </div>
      </div>
    </section>
  );
};

export default EventsSection;
